<script setup lang="ts">
    import { reactive, ref, computed, watch, onMounted, inject } from 'vue';
    import { useRouter, useRoute } from 'vue-router';
    import * as statsApi from './IssueStatistics.access';
    import { getObjectCommunicationTypes } from '../../Event/EventIssueCommunicationType.access';
    import { CommunicationType } from '../../Event/EventTypes';
    import {
        type Issue,
        type IssueReportCounts,
        type IssueStatisticsLink,
        type UserAgentStats,
        type GeoIpTotalStats,
        type TopSubscriber,
        type IssueShare,
        type IssueDailyStatistics,
        type UnsubscribeReasonStats,
        TrackingOptions,
    } from '../interfaces';
    import StatisticsNavbar from './components/StatisticsNavbar.vue';
    import StatisticsHighlights from './components/StatisticsHighlights.vue';
    import StatisticsOverviewChart from './components/StatisticsOverviewChart.vue';
    import StatisticsEventsOverTime from './components/StatisticsEventsOverTime.vue';
    import StatisticsContactList from './components/IssueContactList.vue';
    import StatisticsLinks from './components/StatisticsLinks.vue';
    import StatisticsMailClients from './components/StatisticsMailClients.vue';
    import StatisticsClickMap from './components/StatisticsClickMap.vue';
    import StatisticsGeoIp from './components/StatisticsGeoIp.vue';
    import StatisticsTopSubscribers from './components/StatisticsTopSubscribers.vue';
    import StatisticsShares from './components/StatisticsShares.vue';
    import StatisticsUnsubscribeReasons from './components/StatisticsUnsubscribeReasons.vue';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import Loading from '@shared/components/Loading.vue';
    import StatisticsScheduleReminderModal from './components/StatisticsScheduleReminderModal.vue';
    import StatisticsRecurringEditorModal from './components/StatisticsRecurringEditorModal.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { formatDate } from '@shared/utils/formatDateTime';
    import StatisticsHeader from './components/StatisticsHeader.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ScopeAccess } from '@shared/directives/Scope.access';
    import { Permissions } from '@shared/Root/Permissions';
    import { createIssueFromTemplate, type NewIssuePayload, type IssueDetail } from '../Issues.access';
    import StatisticsContactFilterAlert from '@views/Lists/components/ContactFilterAlert.vue';
    import type { SerializedContactFilterState } from '@views/Lists/components/ContactFilter.vue';
    import { buildDynamicSubjectPresetFilters } from './components/statistics-types';
    import { useContactAdditionalFields } from '@views/Lists/useContactAdditionalFields';
    import { ContextAccess } from '@shared/Root/Context.access';
    import type { DynamicSubjectRow } from '../dynamicSubject';
    import Tabs from '@shared/components/Tabs.vue';
    import Tab from '@shared/components/Tab.vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const aggregateContactFilter = ref('');
    const contactFilterState = ref<Partial<SerializedContactFilterState> | null>(null);
    const navbarRef = ref<InstanceType<typeof StatisticsNavbar>>();
    const contactFilterRef = computed(() => navbarRef.value?.contactFilterRef);

    function onSubjectClick(rows: DynamicSubjectRow[], negate: boolean, label: string) {
        const presets = buildDynamicSubjectPresetFilters(rows, availableFields.value, negate);
        if (!presets.length) return;
        navbarRef.value?.applyPreset(label, presets);
    }
    const { availableFields } = useContactAdditionalFields(contextAccess);

    const props = defineProps<{
        issueId: string;
        contactType?: string;
    }>();

    const router = useRouter();
    const route = useRoute();
    const showContactList = computed(() => !!props.contactType);
    const showingContact = computed(() => route.name === 'IssueStatisticsContact');
    const contactListDateRange = ref<{ start: string | null; end: string | null } | null>(null);

    const effectiveContactListDateRange = computed(() => contactListDateRange.value ?? state.dateRange);

    watch(showContactList, (val) => {
        if (val) {
            const query = { ...route.query };
            if (state.dateRange.start) query.start = state.dateRange.start;
            else delete query.start;
            if (state.dateRange.end) query.end = state.dateRange.end;
            else delete query.end;
            router.replace({ query });
        } else {
            contactListDateRange.value = null;
        }
    });

    function onEventsOverTimeClick(contactType: string, date: string) {
        if (isAnonymousTracking.value) return;
        contactListDateRange.value = { start: date, end: date };
        router.push({ name: 'IssueStatisticsContacts', params: { issueId: props.issueId, contactType }, query: { ...route.query } });
    }

    async function onCreateCopy() {
        if (!state.issue) return;
        const issue = state.issue as IssueDetail;
        const prefix = ($Resources.CopyOfName as string) || '';
        const prefixed = prefix + (issue.Subject || '');
        const subject = issue.Event?.EventId ? issue.Subject : prefixed.length > 250 ? issue.Subject : prefixed;
        const payload: NewIssuePayload = {
            IssueId: issue.IssueId,
            Subject: subject,
            DynamicSubject: issue.DynamicSubject,
            FromName: issue.FromName,
            FromAddress: issue.FromAddress,
            ReplyToAddress: issue.ReplyToAddress,
            Charset: issue.Charset,
            Culture: issue.Culture,
            Category: issue.Category,
            EditableHtml: issue.EditableHtml,
            EditableCss: issue.EditableCss,
            TrackingOptions: issue.TrackingOptions,
            Tags: issue.Tags,
            AccessControlList: issue.AccessControlList,
            Lists: issue.Lists,
            Segments: issue.Segments,
            SegmentId: issue.SegmentId,
            UseGoogleAnalytics: issue.UseGoogleAnalytics,
            GoogleAnalyticsCampaignName: issue.GoogleAnalyticsCampaignName,
            GoogleAnalyticsContent: issue.GoogleAnalyticsContent,
            GoogleAnalyticsTerm: issue.GoogleAnalyticsTerm,
            GoogleAnalyticsSource: issue.GoogleAnalyticsSource,
        };
        try {
            const result = await createIssueFromTemplate(payload);
            document.location.href = `/Issues/${result.IssueId}`;
        } catch {}
    }
    const activeTab = ref<'overview' | 'appearance'>('overview');
    const activeTabIndex = computed(() => (activeTab.value === 'appearance' ? 1 : 0));
    const requestedTabIndex = (() => {
        const idx = parseInt(route.query.t as string);
        return isNaN(idx) ? null : idx;
    })();
    const showScheduleReminderModal = ref(false);
    const showRecurringEditorModal = ref(false);

    const state = reactive({
        loading: false,
        statsLoading: false,
        issue: null,
        overview: null,
        links: [],
        clickMapLinks: [],
        mailClients: null,
        geoIp: null,
        topSubscribers: [],
        shares: [],
        firstDay: [],
        sender: null,
        categoryAverages: null,
        dateRange: { start: null, end: null },
        selectedVariationFilter: null,
        suppressQueryString: true,
        showAllLinks: false,
        loadAllLinks: false,
        clickMapReady: false,
        linkMultipleOccurrences: false,
        unsubscribeReasonStats: [],
        communicationType: null,
    }) as {
        loading: boolean;
        statsLoading: boolean;
        issue: Issue | null;
        communicationType: CommunicationType | null;
        overview: IssueReportCounts | null;
        links: IssueStatisticsLink[];
        clickMapLinks: IssueStatisticsLink[];
        mailClients: UserAgentStats | null;
        geoIp: GeoIpTotalStats | null;
        topSubscribers: TopSubscriber[];
        shares: IssueShare[];
        firstDay: IssueDailyStatistics[];
        sender: string | null;
        categoryAverages: IssueReportCounts | null;
        dateRange: { start: string | null; end: string | null };
        selectedVariationFilter: string | null;
        suppressQueryString: boolean;
        showAllLinks: boolean;
        loadAllLinks: boolean;
        clickMapReady: boolean;
        linkMultipleOccurrences: boolean;
        unsubscribeReasonStats: UnsubscribeReasonStats[];
    };

    function buildQs() {
        return statsApi.buildQueryString(state.dateRange, state.selectedVariationFilter, aggregateContactFilter.value || null);
    }

    let statsAbortController: AbortController | null = null;
    let linksAbortController: AbortController | null = null;

    async function loadEventCommunicationType(issue: Issue) {
        if (!issue.Event?.UseRelativeSchedule) return;
        try {
            const items = await getObjectCommunicationTypes(issue.IssueId);
            state.communicationType = items?.[0]?.CommunicationType ?? null;
        } catch {
            state.communicationType = null;
        }
    }

    async function loadLinks(signal?: AbortSignal) {
        const ownController = signal
            ? null
            : (() => {
                  linksAbortController?.abort();
                  return (linksAbortController = new AbortController());
              })();
        const effectiveSignal = signal ?? ownController!.signal;
        state.loading = true;
        try {
            await statsApi.getLinks(props.issueId, state.suppressQueryString, state.loadAllLinks, buildQs(), effectiveSignal).then((r) => (state.links = r));
        } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') return;
            throw e;
        } finally {
            if (!effectiveSignal.aborted) state.loading = false;
        }
    }

    async function loadAllStats() {
        statsAbortController?.abort();
        statsAbortController = new AbortController();
        const { signal } = statsAbortController;
        state.statsLoading = true;
        try {
            const qs = buildQs();
            await Promise.all([
                statsApi.getOverview(props.issueId, qs, signal).then((r) => (state.overview = r)),
                statsApi.getSender(props.issueId, signal).then((r) => (state.sender = r)),
                statsApi.getMailClients(props.issueId, qs, signal).then((r) => (state.mailClients = r)),
                statsApi.getGeoIp(props.issueId, qs, signal).then((r) => (state.geoIp = r)),
                statsApi.getTopSubscribers(props.issueId, qs, signal).then((r) => (state.topSubscribers = r)),
                statsApi.getShares(props.issueId, qs, signal).then((r) => (state.shares = r)),
                statsApi.getFirstDay(props.issueId, state.dateRange, signal).then((r) => (state.firstDay = r)),
                // statsApi.getCategoryAverages(props.issueId).then((r) => (state.categoryAverages = r)), disabled for now
                loadLinks(signal),
                statsApi.getClickMapLinks(props.issueId, qs, signal).then((r) => (state.clickMapLinks = r)),
                statsApi.getUnsubscribeReasonStats(props.issueId, signal).then((r) => (state.unsubscribeReasonStats = r)),
            ]);
        } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') return;
            throw e;
        } finally {
            if (!signal.aborted) state.statsLoading = false;
        }
    }

    function setupDateRangeWatch() {
        watch(
            () => [state.dateRange.start, state.dateRange.end],
            ([start, end]) => {
                const query = { ...route.query };
                if (start) query.start = start as string;
                else delete query.start;
                if (end) query.end = end as string;
                else delete query.end;
                router.replace({ query });
                if (state.issue) loadAllStats();
            },
            { deep: true }
        );
    }

    function setupContactFilterWatch() {
        watch(
            [aggregateContactFilter, contactFilterState],
            ([f, s]) => {
                const query = { ...route.query };
                if (f) query.contactFilter = f;
                else delete query.contactFilter;
                if (s && Object.keys(s).length > 0) query.contactFilterState = JSON.stringify(s);
                else delete query.contactFilterState;
                router.replace({ query });
            },
            { deep: true }
        );
    }

    function restoreContactFilterFromUrl() {
        if (typeof route.query.contactFilter === 'string') aggregateContactFilter.value = route.query.contactFilter;

        const raw = route.query.contactFilterState;
        if (!raw || typeof raw !== 'string') return;
        try {
            const parsed = JSON.parse(raw) as Partial<SerializedContactFilterState>;
            contactFilterRef.value?.restoreFilterState(parsed);
        } catch {
            /* ignore malformed query string */
        }
    }

    onMounted(async () => {
        state.loading = false;
        state.statsLoading = false;

        if (state.issue) {
            setupDateRangeWatch();
            setupContactFilterWatch();
            return;
        }

        if (route.query.start) state.dateRange.start = route.query.start as string;
        if (route.query.end) state.dateRange.end = route.query.end as string;
        restoreContactFilterFromUrl();
        state.loading = true;
        try {
            state.issue = await statsApi.getIssue(props.issueId);
            await loadEventCommunicationType(state.issue);
            if (state.issue.IsRecurring && !showContactList.value && !state.dateRange.start) {
                const started = state.issue.StartedDateTime ?? state.issue.Started;
                if (started) state.dateRange.start = started.split('T')[0];
            }
            await loadAllStats();
        } finally {
            state.loading = false;
        }

        setupDateRangeWatch();
        setupContactFilterWatch();
    });

    watch(
        () => state.selectedVariationFilter,
        () => {
            if (state.issue) loadAllStats();
        }
    );

    watch(aggregateContactFilter, () => {
        if (state.issue) loadAllStats();
    });

    watch(
        () => state.suppressQueryString,
        () => loadLinks()
    );
    watch(
        () => state.loadAllLinks,
        () => loadLinks()
    );

    const hasDateFilter = computed(() => !!state.dateRange.start || !!state.dateRange.end);

    const canScheduleRecurring = computed(
        () =>
            !!state.issue &&
            !state.issue.Event &&
            !state.issue.Survey &&
            !state.issue.Journey &&
            scopeAccess.hasScope({ Feature: 'Automations', Permission: Permissions.Execute })
    );

    watch(
        () => state.clickMapReady,
        (ready) => {
            if (ready && requestedTabIndex === 1) activeTab.value = 'appearance';
        },
        { immediate: true }
    );

    const isAnonymousTracking = computed(() => {
        const t = state.issue?.TrackingOptions;
        return t === TrackingOptions.Anonymous || t === TrackingOptions.UTMAndAnonymous;
    });

    const hasIssueVariations = computed(() => {
        if (!state.issue?.IssueVariationConfiguration || !state.issue?.UseIssueVariationTest) return false;

        return (
            (state.issue?.IssueVariationConfiguration?.IssueVariations?.filter(
                (variation) => variation.AlternativeSubject !== null && variation.AlternativeSubject !== ''
            )?.length ?? 0) > 1
        );
    });
</script>

<template>
    <StatisticsNavbar
        v-if="!showingContact"
        ref="navbarRef"
        :issue="state.issue"
        :dateRange="state.dateRange"
        :selectedVariationFilter="state.selectedVariationFilter"
        :showContactList="showContactList"
        :contactType="props.contactType"
        :canScheduleRecurring="canScheduleRecurring"
        :hasIssueVariations="hasIssueVariations"
        :availableFields="availableFields"
        @update:dateRange="
            (s, e) => {
                state.dateRange.start = s;
                state.dateRange.end = e;
            }
        "
        @update:selectedVariationFilter="(val) => (state.selectedVariationFilter = val)"
        @update:contactFilter="(f) => (aggregateContactFilter = f)"
        @update:contactFilterState="(s) => (contactFilterState = s)"
        @createCopy="onCreateCopy"
        @openScheduleReminder="showScheduleReminderModal = true"
        @openRecurringEditor="showRecurringEditorModal = true" />

    <template v-if="state.issue">
        <!-- Contact list panel -->
        <StatisticsContactList
            v-if="showContactList"
            :issueId="issueId"
            :contactType="props.contactType!"
            :dateRange="effectiveContactListDateRange"
            :variationFilter="state.selectedVariationFilter"
            :suppressQueryString="state.suppressQueryString"
            :contactFilter="aggregateContactFilter" />

        <!-- Overview panel -->
        <template v-else>
            <StatisticsHeader
                :issue="state.issue"
                :sender="state.sender"
                :hasIssueVariations="hasIssueVariations"
                :communicationType="state.communicationType"
                :onSubjectClick="onSubjectClick" />

            <div class="alert-group" :class="activeTab === 'appearance' && 'm-0-b'">
                <BsAlert
                    icon="filter"
                    :show="hasDateFilter"
                    :type="AlertTypes.Info"
                    :title="$Resources.DateRangeFilterOnAlertHeading"
                    :dismissible="false"
                    :buttonText="$Resources.ClearBtn"
                    @buttonClick="
                        () => {
                            state.dateRange.start = null;
                            state.dateRange.end = null;
                        }
                    ">
                    <template #content>
                        {{ formatDate(state.dateRange.start) || '-' }}
                        <span v-show="state.dateRange.end && state.dateRange.start !== state.dateRange.end">
                            {{ $Resources.DateRangeSeparator }} {{ formatDate(state.dateRange.end) || '-' }}
                        </span>
                    </template>
                </BsAlert>

                <StatisticsContactFilterAlert :filterRef="contactFilterRef" :totalCount="state.overview?.RecipientCount ?? 0" />

                <BsAlert
                    :show="contactFilterRef && contactFilterRef.filterCount > 0"
                    :type="AlertTypes.Info"
                    :title="$Resources.IssueStatsContactFilterAlertTitle"
                    :content="$Resources.IssueStatsContactFilterAlertContent"
                    :dismissible="false" />
            </div>

            <Tabs teleportTarget="#teleported-target-tabs" navClass="navbar-tabs list-unstyled d-inline-flex" :selectedIndex="activeTabIndex">
                <Tab :title="$Resources.StatisticsNavbarTabLabelOverview" @tabClick="activeTab = 'overview'">
                    <!-- Overview tab -->
                    <div class="container-fluid m-lg-t">
                        <StatisticsHighlights
                            :overview="state.overview"
                            :categoryAverages="state.categoryAverages"
                            :isAnonymousTracking="isAnonymousTracking" />
                        
                        <StatisticsOverviewChart :overview="state.overview" :isAnonymousTracking="isAnonymousTracking" :trackingOptions="state.issue.TrackingOptions" />
                        
                        <StatisticsUnsubscribeReasons :unsubscribeReasonStats="state.unsubscribeReasonStats" />

                        <FeatureLock feature="Email.AdvancedStatistics" :hideWhenLocked="true">
                            <StatisticsEventsOverTime :eventsOverTime="state.firstDay" @clickBar="onEventsOverTimeClick" />
                        </FeatureLock>

                        <StatisticsLinks
                            :links="state.links"
                            :issueId="issueId"
                            :suppressQueryString="state.suppressQueryString"
                            :showAllLinks="state.showAllLinks"
                            :loadAllLinks="state.loadAllLinks"
                            :isAnonymousTracking="isAnonymousTracking"
                            @update:suppressQueryString="(val) => (state.suppressQueryString = val)"
                            @update:showAllLinks="(val) => (state.showAllLinks = val)"
                            @update:loadAllLinks="(val) => (state.loadAllLinks = val)" />

                        <FeatureLock feature="Email.AdvancedStatistics" :hideWhenLocked="false">
                            <StatisticsTopSubscribers :topSubscribers="state.topSubscribers" />
                        </FeatureLock>

                        <FeatureLock feature="Email.AdvancedStatistics" :hideWhenLocked="false">
                            <StatisticsShares :shares="state.shares" :isAnonymousTracking="isAnonymousTracking" />
                        </FeatureLock>

                        <StatisticsMailClients :mailClients="state.mailClients" />

                        <FeatureLock feature="Email.AdvancedStatistics" :hideWhenLocked="false">
                            <StatisticsGeoIp :geoIp="state.geoIp" />
                        </FeatureLock>
                    </div>
                </Tab>

                <Tab :title="$Resources.StatisticsNavbarTabLabelAppearance" :disabled="!state.clickMapReady" @tabClick="activeTab = 'appearance'">
                    <!-- Appearance tab (click map) -->
                    <StatisticsClickMap
                        :issue="state.issue"
                        :clickMapLinks="state.clickMapLinks"
                        :overview="state.overview"
                        :show="activeTab === 'appearance'"
                        @ready="state.clickMapReady = true"
                        @update:linkMultipleOccurrences="(val) => (state.linkMultipleOccurrences = val)" />
                </Tab>
            </Tabs>

            <StatisticsScheduleReminderModal
                v-if="!state.issue.Event && !state.issue.Survey"
                :show="showScheduleReminderModal"
                :issue="state.issue"
                @close="showScheduleReminderModal = false" />

            <StatisticsRecurringEditorModal
                v-if="canScheduleRecurring"
                :show="showRecurringEditorModal"
                :issue="state.issue"
                @close="showRecurringEditorModal = false" />
        </template>
    </template>

    <Loading :show="state.loading || state.statsLoading" loadingStyle="progress-bar" :fixed="true" />
</template>
